import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import spIcon from "../../assets/brand/swlogo.png";
import spWordmark from "../../assets/brand/swletters.png";
import api from "../../api/api";

export default function ProfileUpdate() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    username: "",
    phone: "",
    businessName: "",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Fetch existing profile data on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setFetching(true);
        const response = await api.profile.viewProfile();

        if (response?.status === true && response.user) {
          const user = response.user;
          setForm({
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            username: user.userName || "",
            phone: user.phoneNumber || "",
            businessName: user.businessName || "",
          });

          // If avatar exists, you could show it (optional)
          if (user.avatar) {
            setPreviewUrl(user.avatar);
          }
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
        // Don't block user – just show empty form
      } finally {
        setFetching(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Optional: basic validation (image type & size)
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error("Image size should be less than 5MB");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file)); // preview
  };

  const handleSubmit = async () => {
    const { firstName, lastName, username, phone, businessName } = form;

    if (!firstName.trim() || !lastName.trim() || !username.trim() || !phone.trim()) {
      toast.error("Please fill in all required fields (First name, Last name, Username, Phone)");
      return;
    }

    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 13) {
      toast.warn("Please enter a valid phone number");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("firstName", firstName.trim());
      formData.append("lastName", lastName.trim());
      formData.append("userName", username.trim());
      formData.append("phoneNumber", phone.trim());

      if (businessName.trim()) {
        formData.append("businessName", businessName.trim());
      }

      if (selectedFile) {
        formData.append("avatar", selectedFile);
      }

      const response = await api.profile.updateProfileInfo(formData);

      if (response?.status === true) {
        toast.success("Profile updated successfully!");
        localStorage.setItem("hasProfile", "true");
        navigate("/verify/documents");
      } else {
        toast.error(response?.message || "Failed to update profile");
      }
    } catch (err) {
      console.error("Profile update failed:", err);
      toast.error(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
        <div className="text-[#5d4037] text-lg">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="w-[412px] [font-family:'Nunito',Helvetica] text-[#5d4037]">

        {/* Logo */}
        <div className="w-full flex justify-center">
          <div className="inline-flex items-center gap-[0px] leading-none">
            <img
              src={spIcon}
              alt="Sweet Plate logo"
              className="shrink-0 select-none object-contain w-[65px] h-[86px] -mt-[2px]"
            />
            <img
              src={spWordmark}
              alt="Sweet Plate"
              className="shrink-0 select-none object-contain h-[29px] w-auto -ml-[4px]"
            />
          </div>
        </div>

        <h1 className="mt-3 ml-6 font-bold text-[22px] leading-7">
          Join Sweet Plate Vendor
        </h1>

        {/* Stepper */}
        <div className="w-[378px] mx-auto mt-3 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#5d4037] text-[#fff8e1] grid place-items-center text-sm font-bold">
            1
          </div>
          <div className="flex-1 h-[2px] bg-[#5d4037]/40" />
        </div>

        <div className="w-[378px] mx-auto mt-6">
          <p className="font-extrabold uppercase text-[14px] tracking-wide">
            Profile Information
          </p>
          <p className="mt-1 text-[14px]">Please provide your basic contact details</p>
        </div>

        {/* Form */}
        <div className="w-[378px] mx-auto mt-6 space-y-6">

          {/* Avatar Upload */}
          <div className="flex flex-col items-center">
            <label className="text-[14px] font-semibold mb-2 block">
              Profile Picture (optional)
            </label>
            <div className="relative w-28 h-28 rounded-full overflow-hidden border-4 border-[#ccb357] bg-gray-100">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Profile preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                  No photo
                </div>
              )}
            </div>
            <label className="mt-3 cursor-pointer text-[#5d4037] hover:underline text-sm">
              {selectedFile ? "Change photo" : "Upload photo"}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[14px] font-semibold mb-1 block">First Name *</label>
              <input
                name="firstName"
                type="text"
                placeholder="First name"
                value={form.firstName}
                onChange={handleChange}
                className="w-full rounded-[12px] border border-[#5d4037] bg-white p-3 text-[15px] outline-none"
              />
            </div>
            <div>
              <label className="text-[14px] font-semibold mb-1 block">Last Name *</label>
              <input
                name="lastName"
                type="text"
                placeholder="Last name"
                value={form.lastName}
                onChange={handleChange}
                className="w-full rounded-[12px] border border-[#5d4037] bg-white p-3 text-[15px] outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-[14px] font-semibold mb-1 block">Username *</label>
            <input
              name="username"
              type="text"
              placeholder="Choose a username"
              value={form.username}
              onChange={handleChange}
              className="w-full rounded-[12px] border border-[#5d4037] bg-white p-3 text-[15px] outline-none"
            />
          </div>

          <div>
            <label className="text-[14px] font-semibold mb-1 block">Phone Number *</label>
            <input
              name="phone"
              type="tel"
              placeholder="0803 XXX XXXX"
              value={form.phone}
              onChange={handleChange}
              className="w-full rounded-[12px] border border-[#5d4037] bg-white p-3 text-[15px] outline-none"
            />
          </div>

          <div>
            <label className="text-[14px] font-semibold mb-1 block">
              Business / Store Name (optional)
            </label>
            <input
              name="businessName"
              type="text"
              placeholder="e.g. Cyril's Kitchen"
              value={form.businessName}
              onChange={handleChange}
              className="w-full rounded-[12px] border border-[#5d4037] bg-white p-3 text-[15px] outline-none"
            />
          </div>
        </div>

        {/* Button */}
        <div className="w-[378px] mx-auto">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className={`mt-8 w-[290px] h-[60px] mx-auto block rounded-[50px] font-bold transition-all ${
              loading
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-[#f7c945] text-[#5d4037] hover:opacity-90"
            }`}
          >
            {loading ? "Saving..." : "Save & Continue"}
          </button>
        </div>

        <div className="w-[378px] mx-auto mt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mx-auto block text-sm underline text-[#5d4037]"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}